import React from 'react';
import { ReactComponent as ListAltSvg } from '../icons/list_alt.svg';
import { ReactComponent as OutlineInfoSvg } from '../icons/outline_info.svg';
import { ReactComponent as PaymentCardSvg } from '../icons/payment_card.svg';
import { ReactComponent as TimeClockSvg } from '../icons/time_clock.svg';
import { ReactComponent as VideoCameraSvg } from '../icons/video_camera.svg';
import GenericCard, { GenericContentType } from './GenericCard';
import styles from './VirtualVisitCard.module.css';
import careRecommendationConfig from '../resources/careRecommendationsConfig.json';

import { AmpFlagSet } from '../amplitude';

export type VirtualVisitCardData = {
    openHours: string;
    waitTime: string;
    url: string;
    coverageLink: string;
};

export type OldVirtualVisitCardData = {
    renderer: string;
    template: any;
};

export const VirtualVisitCard: React.FC<{
    data: VirtualVisitCardData;
    featureFlags: AmpFlagSet;
}> = ({ data, featureFlags }) => {
    if (data.waitTime === 'closed') {
        return (
            <GenericCard
                data={{
                    title: {
                        text: careRecommendationConfig.providence
                            .expresscareVirtual,
                    },
                    subtitles: [{ text: data.openHours }],
                    content: [
                        {
                            type: GenericContentType.Info,
                            title: {
                                text: careRecommendationConfig.providence
                                    .virtualClosed,
                            },
                            info: { text: '' },
                            image: (
                                <TimeClockSvg
                                    className={styles.virtualVisitContentIcon}
                                />
                            ),
                        },
                        {
                            type: GenericContentType.Info,
                            title: {
                                text: careRecommendationConfig.providence
                                    .virtualCost,
                            },
                            info: {
                                text: careRecommendationConfig.providence
                                    .virtualInsurance,
                            },
                            image: (
                                <PaymentCardSvg
                                    className={styles.virtualVisitContentIcon}
                                />
                            ),
                        },
                    ],
                    buttonData: {
                        title: careRecommendationConfig.providence
                            .virtualLearnMore,
                        url: data.url,
                        amplitudeEvent: {
                            eventType: 'ECV - Link Click',
                            eventProperties: {
                                link_url: data.url,
                                ecv_waitTime: 'Closed',
                            },
                        },
                    },
                }}
                featureFlags={featureFlags}
            />
        );
    }

    return (
        <GenericCard
            data={{
                title: {
                    text: careRecommendationConfig.providence
                        .expresscareVirtual,
                },
                subtitles: [{ text: data.openHours }],
                content: [
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: careRecommendationConfig.providence
                                .virtualVideoVisit,
                        },
                        info: {
                            text: careRecommendationConfig.providence
                                .virtualVideoDescription,
                        },
                        image: (
                            <VideoCameraSvg
                                className={styles.virtualVisitContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: careRecommendationConfig.providence
                                .virtualCost,
                        },
                        info: {
                            text: careRecommendationConfig.providence
                                .virtualInsurance,
                        },
                        image: (
                            <PaymentCardSvg
                                className={styles.virtualVisitContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: careRecommendationConfig.providence
                                .virtualCannotAddress,
                        },
                        info: {
                            text: careRecommendationConfig.providence
                                .virtualCannotAddressInfo,
                        },
                        image: (
                            <OutlineInfoSvg
                                className={styles.virtualVisitContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: careRecommendationConfig.providence
                                .virtualCurrentWait,
                        },
                        info: { text: data.waitTime },
                        image: (
                            <TimeClockSvg
                                className={styles.virtualVisitContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Link,
                        title: careRecommendationConfig.providence
                            .virtualCoverage,
                        linkUrl: data.coverageLink,
                        image: (
                            <ListAltSvg
                                className={styles.virtualVisitContentIcon}
                            />
                        ),
                    },
                ],
                buttonData: {
                    title: careRecommendationConfig.providence.virtualStart,
                    url: data.url,
                    amplitudeEvent: {
                        eventType: 'ECV - Link Click',
                        eventProperties: {
                            link_url: data.url,
                            ecv_waitTime: 'Current wait ' + data.waitTime,
                        },
                    },
                },
            }}
            featureFlags={featureFlags}
        />
    );
};

export const OldVirtualVisitCard: React.FC<{
    data: OldVirtualVisitCardData;
}> = ({ data }) => {
    const template = data.template;
    return (
        <div className={styles.virtualVisitCard}>
            <p>
                <strong>{template.headline}</strong>
            </p>
            <p>{template.headerText}</p>
            <h1>{template.expressCareTitle}</h1>
            <h3>{template.section1Line1}</h3>
            <h4>{template.section1Line2}</h4>
            <h3>{template.expressCareCost}</h3>
            <h4>{template.expressCareLine2}</h4>
            <br />
            <h3>{template.section2Line1}</h3>
            <h4>{template.section2Line2}</h4>
            <h4>{template.insuranceLine1}</h4>
            <h4>{template.insuranceLine2}</h4>
            <h4>
                <a
                    href={template.insuranceLine3Link}
                    rel="noopener noreferrer nofollow"
                    target="_blank"
                >
                    {template.insuranceLine3Text}
                </a>
            </h4>
            <p className="footer">{template.footerText}</p>
        </div>
    );
};
